<template>
  <div class="delivery-page">
    <Button class="back-button" @click="router.back()" severity="secondary" label="Retour" icon="pi pi-arrow-left" />

    <!-- Bannière mode hors ligne -->
    <div v-if="isUserOfflineMode" class="offline-banner mx-4 my-2">
      <i class="pi pi-info-circle mr-2"></i>
      <div>
        <div class="font-bold">Mode hors ligne activé</div>
        <div class="text-xs">Les données affichées sont celles disponibles en local. Les modifications seront synchronisées quand vous passerez en mode en ligne.</div>
      </div>
    </div>

    <!-- Bannière connexion perdue -->
    <div v-else-if="!isOnline" class="connection-lost-banner mx-4 my-2">
      <i class="pi pi-exclamation-triangle mr-2"></i>
      <div>
        <div class="font-bold">Connexion internet perdue</div>
        <div class="text-xs">Vous pouvez quand même effectuer des modifications, elles seront synchronisées automatiquement dès que la connexion sera rétablie.</div>
      </div>
    </div>

    <!-- Bannière erreurs de synchronisation -->
    <div v-if="hasSyncErrors" class="sync-error-banner mx-4 my-2">
      <i class="pi pi-exclamation-circle mr-2"></i>
      <div>
        <div class="font-bold">Erreur de synchronisation</div>
        <div class="text-xs">Des changements pour cette livraison n'ont pas pu être synchronisés après plusieurs tentatives.</div>
        <Button label="Réessayer maintenant" icon="pi pi-refresh" class="p-button-sm p-button-danger mt-2" @click="retrySync" :disabled="!isOnline || isSyncing" />
      </div>
    </div>

    <!-- Bannière changements en attente -->
    <div v-else-if="hasLocalChanges" class="pending-changes-banner mx-4 my-2">
      <i class="pi pi-clock mr-2"></i>
      <div>
        <div class="font-bold">Modifications en attente</div>
        <div class="text-xs">Des modifications ont été effectuées et sont en attente de synchronisation.</div>
        <Button label="Synchroniser maintenant" icon="pi pi-sync" class="p-button-sm p-button-info mt-2" @click="syncPendingUpdates" :disabled="!isOnline || isSyncing" />
      </div>
    </div>

    <ProgressSpinner v-if="loading" class="w-full flex justify-center my-4" />

    <div v-else-if="currentDelivery" class="delivery-container">

      <div class="cards-container">
        <DeliveryInfoCard :delivery="mappedDelivery" />
        <NearestLocationCard 
          v-if="hasNearestLocation" 
          :nearestLocation="nearestLocation" 
          :shopLocation="shopLocation"
        />
        <DeliveryAssistanceCard :delivery="mappedDelivery" />
      </div>

      <!-- Modale Problème -->
      <Dialog v-model:visible="showProblemModal" modal header="Signaler un problème" position="top"
        :style="{ width: '90vw', maxWidth: '500px' }">
        <ShopDeliveryProblemModal ref="problemModalRef" :delivery="currentDelivery" />
        <template #footer>
          <div class="dialog-footer">
            <div class="footer-content">
              <p v-if="isProblemFormValid" class="warning-text">
                Si le problème n'est pas justifié cela peut impacter votre prime
              </p>
              <div class="footer-buttons">
                <Button label="Retour" icon="pi pi-times" severity="secondary" text @click="closeProblemModal" />
                <Button label="Signaler" icon="pi pi-check" severity="danger" @click="handleProblemConfirm"
                  :disabled="!isProblemFormValid" />
              </div>
            </div>
          </div>
        </template>
      </Dialog>
      

      <!-- Modale Confirmation -->
      <Dialog v-model:visible="showConfirmModal" modal header="Valider la livraison" position="top"
        :style="{ width: '90vw', maxWidth: '500px' }">
        <ShopDeliveryConfirmModal ref="confirmModalRef" :delivery="currentDelivery" />
        <template #footer>
          <div class="dialog-footer">
            <div class="footer-content">
              <div class="footer-buttons">
                <Button label="Annuler" icon="pi pi-times" severity="secondary" text @click="closeConfirmModal" />
                <Button label="Confirmer" icon="pi pi-check" severity="success" @click="handleConfirmDelivery"
                  :disabled="!isConfirmFormValid" />
              </div>
            </div>
          </div>
        </template>
      </Dialog>

      <!-- Boutons d'action fixes -->
      <div class="action-buttons">
        <Button label="Valider la livraison" icon="pi pi-check" severity="success" class="validate-button"
          @click="openConfirmModal" />
        <Button label="Signaler un problème" icon="pi pi-times" severity="danger" class="problem-button"
          @click="openProblemModal" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import ProgressSpinner from 'primevue/progressspinner'
import DeliveryInfoCard from './DeliveryDetail/DeliveryInfoCard.vue'
import DeliveryAssistanceCard from './DeliveryDetail/DeliveryAssistanceCard.vue'
import ShopDeliveryProblemModal from './ShopDeliveryDetail/ShopDeliveryProblemModal.vue'
import ShopDeliveryConfirmModal from './ShopDeliveryDetail/ShopDeliveryConfirmModal.vue'
import { useShopDeliverySync } from './ShopDeliveryDetail/composables/useShopDeliverySync'
import NearestLocationCard from './ShopDeliveryDetail/NearestLocationCard.vue'

// Composables
const store = useStore()
const router = useRouter()
const route = useRoute()
const toast = useToast()
const { 
  isSyncing, 
  hasLocalChanges, 
  hasSyncErrors, 
  isOnline, 
  isUserOfflineMode,
  updateShopDelivery,
  checkPendingUpdates,
  syncPendingUpdates
} = useShopDeliverySync()

// Reactive state
const loading = ref(false)
const currentDelivery = computed(() => store.state.currentDelivery)

// Mapper les données de shop delivery au format attendu par les composants standard
const mappedDelivery = computed(() => {
  if (!currentDelivery.value) return null;
  
  console.log("Mapping shop delivery data:", currentDelivery.value);
  
  // Extraire l'adresse du shop_delivery
  const shopAddress = currentDelivery.value.shop_delivery_address || {};
  
  return {
    id: currentDelivery.value.id,
    status: currentDelivery.value.status || 'planned',
    route_position: currentDelivery.value.route_position || 0,
    // Structure d'organisation
    organisation: {
      id: shopAddress.id || 0,
      name: shopAddress.name || 'Boutique'
    },
    // Structure de deliveryLocation
    deliveryLocation: {
      id: shopAddress.id || 0,
      name: shopAddress.name || 'Boutique',
      address: shopAddress.address || '',
      zip_code: shopAddress.zip_code || '',
      city: shopAddress.city || '',
      country: shopAddress.country || 'France',
      delivery_instructions: shopAddress.instructions || '',
      latitude: shopAddress.latitude || null,
      longitude: shopAddress.longitude || null,
      telephone: shopAddress.delivery_phone || '',
      first_name: shopAddress.delivery_first_name || '',
      last_name: shopAddress.delivery_last_name || ''
    },
    // Liste des articles à livrer
    package_deliveries: (currentDelivery.value.items || []).map(item => ({
      id: item.id,
      quantity: item.quantity || 1,
      package: {
        name: item.pack?.name || 'Article'
      }
    }))
  };
})

// Récupérer l'entreprise la plus proche
const nearestLocation = computed(() => {
  if (!currentDelivery.value?.shop_delivery_address?.nearest_delivery_location) {
    return null;
  }
  return currentDelivery.value.shop_delivery_address.nearest_delivery_location;
});

// Vérifier si l'entreprise la plus proche existe
const hasNearestLocation = computed(() => {
  return !!nearestLocation.value;
});

// Extraire les coordonnées de l'adresse de la shop delivery pour le calcul de distance
const shopLocation = computed(() => {
  if (!currentDelivery.value?.shop_delivery_address) {
    return null;
  }
  return {
    latitude: currentDelivery.value.shop_delivery_address.latitude,
    longitude: currentDelivery.value.shop_delivery_address.longitude
  };
});

// Refs pour les modales
const problemModalRef = ref(null)
const confirmModalRef = ref(null)
const showProblemModal = ref(false)
const showConfirmModal = ref(false)

// État du formulaire
const isProblemFormValid = computed(() => {
  return problemModalRef.value?.isFormValid ?? false
})

const isConfirmFormValid = computed(() => {
  return confirmModalRef.value?.isFormValid ?? false
})

// Fonction pour resynchroniser en cas d'erreur
const retrySync = async () => {
  await syncPendingUpdates()
}

// Récupérer les données de livraison
const fetchDelivery = async () => {
  const deliveryId = route.params.id;
  if (!deliveryId) return;
  
  loading.value = true;
  
  try {
    // Vérifier les mises à jour en attente
    await checkPendingUpdates(deliveryId);
    
    // Si la livraison est déjà dans le store et a des propriétés, on la garde
    if (store.state.currentDelivery?.id === deliveryId && 
        store.state.currentDelivery?.shop_delivery_address) {
      console.log("Utilisation des données de livraison shop déjà en mémoire");
      loading.value = false;
      return;
    }
    
    // Récupérer la livraison depuis l'API ou le cache
    try {
      console.log(`Tentative de récupération de la livraison shop ${deliveryId}`);
      const deliveryData = await store.dispatch('sync/getShopDelivery', deliveryId);
      
      if (deliveryData) {
        console.log(`Données de livraison shop récupérées avec succès:`, deliveryData);
        store.commit('SET_CURRENT_DELIVERY', deliveryData);
      } else {
        throw new Error("Aucune donnée de livraison shop trouvée");
      }
    } catch (error) {
      console.error("Échec de récupération de la livraison shop:", error);
      
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de récupérer les données de livraison shop. Veuillez réessayer.',
        life: 3000
      });
      
      // Attendre un moment avant de rediriger
      setTimeout(() => {
        router.back();
      }, 1000);
    }
  } catch (error) {
    console.error('Erreur lors de la récupération de la livraison shop:', error);
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Une erreur est survenue lors du chargement des données',
      life: 3000
    });
    
    // Redirection à la page précédente en cas d'erreur
    setTimeout(() => {
      router.back();
    }, 1000);
  } finally {
    loading.value = false;
  }
}

// Ouverture modale problème
const openProblemModal = () => {
  showProblemModal.value = true
}

// Fermeture modale problème
const closeProblemModal = () => {
  showProblemModal.value = false
}

// Confirmation problème
const handleProblemConfirm = async () => {
  try {
    const result = await problemModalRef.value?.handleConfirm()
    
    if (result?.success) {
      // Fermer la modale
      closeProblemModal()
      
      // Mettre à jour la livraison
      await updateShopDelivery(currentDelivery.value, {
        status: 'NOT DELIVERED',
        undone_reason: result.undoneReason,
      })
      
      // Redirection
      router.back()
    }
  } catch (error) {
    console.error('Erreur lors du signalement:', error)
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Une erreur est survenue lors du signalement',
      life: 3000
    })
  }
}

// Ouverture modale confirmation
const openConfirmModal = () => {
  showConfirmModal.value = true
}

// Fermeture modale confirmation
const closeConfirmModal = () => {
  showConfirmModal.value = false
}

// Confirmation livraison
const handleConfirmDelivery = async () => {
  if (!confirmModalRef.value) {
    console.error('Référence à la modale non disponible')
    return
  }
  
  try {
    const result = await confirmModalRef.value.handleConfirm()
    
    if (result?.success) {
      // Fermer la modale
      closeConfirmModal()
      
      // Mettre à jour la livraison
      await updateShopDelivery(currentDelivery.value, {
        status: 'delivered',
        delivered: true,
        // photo_emplacement: result.locationPhoto,
        // delivery_comment: result.comment
      })
      
      // Redirection
      router.back()
    }
  } catch (error) {
    console.error('Erreur lors de la validation:', error)
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Une erreur est survenue lors de la validation',
      life: 3000
    })
  }
}

onMounted(async () => {
  await fetchDelivery()
  
  // Écouter les changements de connexion
  window.addEventListener('online', async () => {
    if (currentDelivery.value?.id) {
      await checkPendingUpdates(currentDelivery.value.id)
    }
  })
})
</script>

<style scoped>
.delivery-page {
  padding-bottom: 4rem;
  width: 100%;
  max-width: 100%;
}

.delivery-container {
  width: 100%;
  max-width: 100%;
}

.cards-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  width: 100%;
  max-width: 100%;
}

.back-button {
  width: 100%;
  justify-content: flex-start !important;
  padding: 0.5rem 1rem !important;
  border-radius: 0 !important;
  font-size: 1rem;
  border-bottom: 1px solid var(--surface-border);
}

.back-button .pi {
  margin-right: 0.5rem;
}

.action-buttons {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 1rem;
  padding: 0.5rem;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
}

.validate-button,
.problem-button {
  flex: 1;
  padding: 0.75rem 1rem;
  height: 3.5rem;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1rem;
}

.warning-text {
  color: var(--red-600);
  font-size: 0.875rem;
  margin: 0;
  text-align: center;
  font-style: italic;
  border: 2px solid var(--red-600);
  padding: 0.5rem;
  border-radius: 4px;
  background-color: var(--red-50);
  padding-bottom: 1rem;
}

/* Styles pour les bannières */
.offline-banner,
.connection-lost-banner,
.pending-changes-banner,
.sync-error-banner {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
}

.offline-banner {
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
}

.connection-lost-banner {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.pending-changes-banner {
  background-color: #cce5ff;
  border: 1px solid #b8daff;
  color: #004085;
}

.sync-error-banner {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

@media (max-width: 768px) {
  .validate-button,
  .problem-button {
    height: 4rem;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .action-buttons {
    padding: 0.75rem;
  }

  .validate-button .pi,
  .problem-button .pi {
    font-size: 1.25rem;
    margin-right: 0.5rem;
  }
}

@media (min-width: 768px) {
  .back-button {
    max-width: 200px;
    border-radius: var(--border-radius) !important;
    margin: 1rem;
  }
}
</style> 